import { useCallback, useEffect, useMemo, useState } from 'react';

export type RowTreeData = {
  id: string;
  isParent: boolean;
  isExpanded: boolean;
  hidden?: boolean;
  parentId?: string;
  isChild?: boolean;
  toggleExpand?: (parentId: string) => void;
};

export function useTreeData<Item extends RowTreeData>(items: Item[]) {
  const [rows, setRows] = useState<Item[]>([]);

  const toggleExpand = useCallback((parentId: string) => {
    setRows((previousRows) => {
      const parent = previousRows.find((row) => row.id === parentId);

      if (!parent) {
        return previousRows;
      }

      if (parent.isExpanded) {
        const descendantIds = new Set<string>();
        const collectDescendants = (pid: string) => {
          for (const row of previousRows) {
            if (row.parentId === pid) {
              descendantIds.add(row.id);
              collectDescendants(row.id);
            }
          }
        };
        collectDescendants(parentId);

        return previousRows.map((row) => {
          if (row.id === parentId) {
            return { ...row, isExpanded: false };
          }
          if (descendantIds.has(row.id)) {
            return { ...row, hidden: true, isExpanded: false };
          }

          return row;
        });
      }

      return previousRows.map((row) => {
        if (row.id === parentId) {
          return { ...row, isExpanded: true };
        }
        if (row.parentId === parentId) {
          return { ...row, hidden: false };
        }

        return row;
      });
    });
  }, []);

  useEffect(() => {
    setRows((previousRows) => {
      const expandedIds = new Set(
        previousRows.filter((row) => row.isExpanded).map((row) => row.id),
      );

      return items.map((row) => {
        const isExpanded = row.isParent && expandedIds.has(row.id);
        const hidden = row.parentId ? !expandedIds.has(row.parentId) : false;

        return {
          ...row,
          isExpanded,
          hidden,
          toggleExpand: row.isParent ? () => toggleExpand(row.id) : undefined,
        };
      });
    });
  }, [items, toggleExpand]);

  return useMemo(() => rows.filter((row) => !row.hidden), [rows]);
}
